import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { cn, formatDateTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import EmptyState from "@/components/common/EmptyState";
import {
  Warehouse,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  Package,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

const CHANNEL_PREFIXES = [
  { prefix: /^WC-/i, channel: "woocommerce" },
  { prefix: /^HB-/i, channel: "hepsiburada" },
  { prefix: /^TY-?/i, channel: "trendyol" },
  { prefix: /^N11-/i, channel: "n11" },
  { prefix: /^AZ-/i, channel: "amazon" },
];

function parseOrderReason(reason) {
  if (!reason) return null;
  const match = reason.match(/Sipariş:\s*(.+)/i);
  if (!match) return null;
  const raw = match[1].trim();
  for (const { prefix, channel } of CHANNEL_PREFIXES) {
    if (prefix.test(raw)) {
      return { channel, number: "#" + raw.replace(prefix, "") };
    }
  }
  return { channel: null, number: "#" + raw };
}

export default function StockPage() {
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_id: "",
    movement_type: "in",
    quantity: "",
    reason: "",
  });
  const [movementPage, setMovementPage] = useState(1);
  const movementPerPage = 10;

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    setMovementPage(1);
  }, [movements.length]);

  const fetchData = async () => {
    try {
      const [movementsRes, productsRes] = await Promise.allSettled([
        api.get("/stock/movements"),
        api.get("/products"),
      ]);

      if (movementsRes.status === "fulfilled") {
        setMovements(Array.isArray(movementsRes.value) ? movementsRes.value : []);
      }
      if (productsRes.status === "fulfilled") {
        setProducts(Array.isArray(productsRes.value) ? productsRes.value : []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.product_id || !formData.quantity || !formData.reason) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }

    try {
      await api.post("/stock/adjust", null, {
        params: {
          product_id: formData.product_id,
          quantity: parseInt(formData.quantity),
          movement_type: formData.movement_type,
          reason: formData.reason,
        },
      });
      
      toast.success("Stok güncellendi!");
      setDialogOpen(false);
      setFormData({ product_id: "", movement_type: "in", quantity: "", reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.message || "Stok güncellenemedi");
    }
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case "in": return <ArrowUpCircle className="h-5 w-5 text-green-600" />;
      case "out": return <ArrowDownCircle className="h-5 w-5 text-red-600" />;
      default: return <RefreshCw className="h-5 w-5 text-blue-600" />;
    }
  };

  const getMovementLabel = (type) => {
    switch (type) {
      case "in": return "Giriş";
      case "out": return "Çıkış";
      default: return "Düzeltme";
    }
  };

  const stats = {
    totalProducts: products.length,
    lowStock: products.filter(p => p.stock > 0 && p.stock < 10).length,
    outOfStock: products.filter(p => p.stock === 0).length,
    todayIn: movements.filter(m => m.movement_type === "in" && new Date(m.created_at).toDateString() === new Date().toDateString()).reduce((sum, m) => sum + m.quantity, 0),
    todayOut: movements.filter(m => m.movement_type === "out" && new Date(m.created_at).toDateString() === new Date().toDateString()).reduce((sum, m) => sum + m.quantity, 0),
  };
  const movementPages = Math.max(1, Math.ceil(movements.length / movementPerPage));
  const movementStart = (movementPage - 1) * movementPerPage;
  const pagedMovements = movements.slice(movementStart, movementStart + movementPerPage);

  const productMap = Object.fromEntries(
    products.map(p => [p.id, p])
  );

  return (
    <div className="page-shell space-y-6" data-testid="stock-page">
      {/* Header */}
      <div className="page-header">
        <div className="app-page-header">
          <h1 className="app-page-title">Stok & Depo</h1>
          <p className="app-page-subtitle">Stok hareketlerini takip edin ve yönetin.</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="add-movement-btn">
              <RefreshCw className="mr-2 h-4 w-4" />
              Stok Hareketi Ekle
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Stok Hareketi Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Ürün</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                >
                  <SelectTrigger data-testid="product-select">
                    <SelectValue placeholder="Ürün seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} (Stok: {p.stock})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Hareket Tipi</Label>
                <Select
                  value={formData.movement_type}
                  onValueChange={(value) => setFormData({ ...formData, movement_type: value })}
                >
                  <SelectTrigger data-testid="movement-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Stok Girişi</SelectItem>
                    <SelectItem value="out">Stok Çıkışı</SelectItem>
                    <SelectItem value="adjustment">Sayım Düzeltmesi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Miktar</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  data-testid="quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Input
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Örn: Tedarikçi siparişi, Sayım düzeltmesi"
                  data-testid="reason-input"
                />
              </div>
              <Button onClick={handleSubmit} className="w-full" data-testid="submit-movement-btn">
                Kaydet
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <div className="app-stat-card p-4" data-testid="stat-total">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-[0.07em] font-semibold">Toplam Ürün</p>
              <p className="app-stat-value mt-1">{stats.totalProducts}</p>
            </div>
            <Package className="h-7 w-7 text-muted-foreground/50" />
          </div>
        </div>
        <div className="app-stat-card p-4" data-testid="stat-low">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-[0.07em] font-semibold">Düşük Stok</p>
              <p className="app-stat-value mt-1 text-amber-600">{stats.lowStock}</p>
            </div>
            <Warehouse className="h-7 w-7 text-amber-400/60" />
          </div>
        </div>
        <div className="app-stat-card p-4" data-testid="stat-out">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-[0.07em] font-semibold">Stok Bitti</p>
              <p className="app-stat-value mt-1 text-rose-600">{stats.outOfStock}</p>
            </div>
            <Package className="h-7 w-7 text-rose-400/60" />
          </div>
        </div>
        <div className="app-stat-card p-4" data-testid="stat-in-today">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-[0.07em] font-semibold">Bugün Giriş</p>
              <p className="app-stat-value mt-1 text-emerald-600">+{stats.todayIn}</p>
            </div>
            <TrendingUp className="h-7 w-7 text-emerald-400/60" />
          </div>
        </div>
        <div className="app-stat-card p-4" data-testid="stat-out-today">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-[0.07em] font-semibold">Bugün Çıkış</p>
              <p className="app-stat-value mt-1 text-rose-600">-{stats.todayOut}</p>
            </div>
            <TrendingDown className="h-7 w-7 text-rose-400/60" />
          </div>
        </div>
      </div>

      {/* Movements Table */}
      <div className="table-shell overflow-hidden">
        <div className="sheet-heading">
          <div>
            <h2>Stok Hareketleri</h2>
            <p>Ürün bazlı giriş, çıkış ve sayım düzeltmeleri</p>
          </div>
        </div>
        <div>
          <div className="divide-y">
            {pagedMovements.map((movement) => (
              <div
                key={movement.id}
                className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors"
                data-testid={`movement-row-${movement.id}`}
              >
                <div className="flex items-center gap-4">
                  {getMovementIcon(movement.movement_type)}
                  {(() => {
                    const prod = productMap[movement.product_id];
                    const img = prod?.image_url || (prod?.images?.[0]) || null;
                    const name = movement.product_name || prod?.name || prod?.title || null;
                    return (
                      <div className="flex items-center gap-3">
                        {img ? (
                          <img src={img} alt={name || ""} className="w-9 h-9 rounded-lg object-cover border border-border shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 text-xs font-semibold text-muted-foreground">
                            {(name || "?")[0]?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{name || "Bilinmeyen Ürün"}</p>
                          {(() => {
                            const parsed = parseOrderReason(movement.reason);
                            if (parsed) {
                              return (
                                <div className="flex items-center gap-1 mt-0.5">
                                  {parsed.channel && (
                                    <img
                                      src={`/integrations/${parsed.channel}.svg`}
                                      alt={parsed.channel}
                                      className="w-3.5 h-3.5 object-contain"
                                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                  )}
                                  <span className="text-xs text-muted-foreground">{parsed.number}</span>
                                </div>
                              );
                            }
                            return <p className="text-sm text-muted-foreground">{movement.reason}</p>;
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="text-right">
                  <p className={cn(
                    "font-semibold",
                    movement.movement_type === "in" ? "text-green-600" : 
                    movement.movement_type === "out" ? "text-red-600" : "text-blue-600"
                  )}>
                    {movement.movement_type === "in" ? "+" : movement.movement_type === "out" ? "-" : ""}
                    {movement.quantity} adet
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(movement.created_at)}
                  </p>
                </div>
              </div>
            ))}

            {movements.length === 0 && (
              <EmptyState
                icon={<RefreshCw strokeWidth={1.5} />}
                title="Henüz stok hareketi yok"
                desc="Stok transferi veya düzeltmesi yaptıkça hareketler burada görünecek."
                size="sm"
              />
            )}
          </div>
          {movements.length > 0 && (
            <div className="flex items-center justify-between border-t px-5 py-3 text-sm text-muted-foreground">
              <span>{movements.length} kayıt · Sayfa {movementPage} / {movementPages}</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" disabled={movementPage <= 1} onClick={() => setMovementPage((p) => Math.max(1, p - 1))}>Önceki</Button>
                <Button variant="ghost" size="sm" disabled={movementPage >= movementPages} onClick={() => setMovementPage((p) => Math.min(movementPages, p + 1))}>Sonraki</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
